import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getMeta, listCases, getStoredEmail, setStoredEmail } from "@/lib/api";

const STATUS_STYLE = {
    submitted: "border-white/40 text-white/80",
    analyzing: "border-[#FF3B30] text-[#FF3B30] animate-pulse",
    analyzed: "bg-white text-black border-white",
    matched: "bg-[#FF3B30] text-white border-[#FF3B30]",
};

export default function Dashboard() {
    const [meta, setMeta] = useState(null);
    const [email, setEmail] = useState(getStoredEmail());
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async (e) => {
        setLoading(true);
        try {
            const data = await listCases(e || undefined);
            setCases(data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        load(email);
        const t = setInterval(() => load(email), 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const apply = () => {
        setStoredEmail(email || "");
        load(email);
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                    MY CASES
                </div>
                <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-none mb-3">
                    Your case <span className="text-[#FF3B30]">file</span>.
                </h1>
                <p className="font-sans text-white/60 max-w-2xl mb-8">
                    No login required. Cases are filtered by your email if provided. Cases without an email are also listed (public anonymous view).
                </p>

                <div className="border-2 border-white/20 p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-end" data-testid="dashboard-filter">
                    <div className="md:col-span-2">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                            FILTER BY EMAIL (OPTIONAL)
                        </div>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com — leave blank for anonymous"
                            className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
                            data-testid="dashboard-email"
                        />
                    </div>
                    <div className="flex gap-3">
                        <button onClick={apply} className="btn-brutal btn-brutal-red" data-testid="dashboard-apply">
                            LOAD CASES
                        </button>
                        <Link to="/hpm-submit" className="btn-brutal" data-testid="dashboard-new-case">
                            <Search size={14} /> NEW
                        </Link>
                    </div>
                </div>

                <div data-testid="cases-list">
                    {loading && (
                        <div className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">
                            LOADING...
                        </div>
                    )}
                    {!loading && cases.length === 0 && (
                        <div className="border-2 border-dashed border-white/20 p-16 text-center">
                            <div className="font-anton text-3xl uppercase mb-3">No cases yet.</div>
                            <p className="font-sans text-white/60 mb-6">Submit your first case to engage the 10 bots.</p>
                            <Link to="/hpm-submit" className="btn-brutal btn-brutal-red">
                                START A CASE <ArrowRight size={16} />
                            </Link>
                        </div>
                    )}

                    <div className="border-2 border-white/20">
                        {cases.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/hpm-analysis/${c.id}`}
                                data-testid={`case-row-${i}`}
                                className={`block p-6 border-white/20 [&:not(:last-child)]:border-b-2 hover:bg-white/5 transition-colors`}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline">
                                    <div className="md:col-span-1 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                                        #{String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="md:col-span-5">
                                        <div className="font-anton text-2xl uppercase leading-none">
                                            {c.case_title}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-2">
                                            {c.case_type} / {c.jurisdiction} / {c.language}
                                        </div>
                                    </div>
                                    <div className="md:col-span-3 font-sans text-sm text-white/70">
                                        {c.parties_plaintiff} <span className="text-white/30">v.</span> {c.parties_defendant}
                                    </div>
                                    <div className="md:col-span-2">
                                        <span
                                            className={`inline-block font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border-2 ${STATUS_STYLE[c.status] || "border-white/40"}`}
                                        >
                                            {c.status === "analyzed" ? `SCORE ${c.strength_score}/100` : c.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="md:col-span-1 text-right">
                                        <ArrowRight size={18} className="inline-block text-white/60" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
